import { useLayoutEffect, useRef, useState, type RefObject } from "react";

/**
 * Word gap used by the animated headline components. `BlurText` lays its words
 * out as flex items separated by a `0.3em` right margin rather than by real
 * spaces, and `WordListSwap` separates its words with a literal space (~0.26em
 * in Poppins). Measuring everything with the wider of the two keeps the result
 * conservative — the fitted size is never a hair too large to fit.
 */
const WORD_GAP_EM = 0.3;

/**
 * Largest font size, in pixels, at which every string in `texts` fits on one
 * line inside `hostRef` — capped at the size CSS already asked for, so this only
 * ever shrinks type, never inflates it.
 *
 * The hero headline is one sentence split across two animated components, and
 * its length swings wildly by locale: English runs 27 characters ("Precision
 * Heat Transfer for") while Italian reaches 41 ("Trasferimento di calore di
 * precisione per"). At a fixed phone type scale the longer ones wrap, and
 * because the components lay their words out with `flex-wrap` the break lands
 * wherever the words happen to run out — in English that stranded "for" alone
 * on a second line, adrift from the rotating word it introduces.
 *
 * No static scale fixes that: one small enough for Italian on a 320px phone
 * would waste half the width in English. So the size is measured instead. Every
 * candidate string is laid out off-screen at the CSS size, and the widest one
 * decides the ratio applied to the whole headline. Passing the rotating words in
 * alongside the static line matters twice over — they share one scale so the two
 * lines still read as a single sentence, and a line sized for the longest word
 * cannot change height as the words cycle, which is what previously forced a
 * two-line reservation to stop the page from jolting on every rotation.
 *
 * Returns `null` for the first render only, meaning "no measurement yet, use the
 * CSS size". Measuring in a layout effect means React re-renders with the real
 * number before the browser paints, so the unfitted size is never seen.
 */
export function useFitToWidth(
	hostRef: RefObject<HTMLElement | null>,
	texts: string[],
): number | null {
	const [fontSize, setFontSize] = useState<number | null>(null);

	// Read through a ref so the effect depends only on the *content* of `texts`,
	// not on the fresh array i18next hands back on every render.
	const textsRef = useRef(texts);
	textsRef.current = texts;
	const textsKey = texts.join("\n");

	useLayoutEffect(() => {
		const host = hostRef.current;
		if (!host) return;

		const fit = () => {
			const available = host.clientWidth;
			if (!available) return;

			const style = getComputedStyle(host);
			const base = parseFloat(style.fontSize);
			if (!base) return;

			// A hidden probe rather than a canvas: canvas measurement knows nothing
			// about letter-spacing or the per-word gaps, both of which are large
			// enough here to decide whether a line fits. `nowrap` keeps the probe on
			// one line so it reports the full natural width instead of being
			// shrink-to-fit down to the column.
			const probe = document.createElement("span");
			probe.style.cssText =
				"position:absolute;top:0;left:0;visibility:hidden;white-space:nowrap;pointer-events:none";
			probe.style.fontFamily = style.fontFamily;
			probe.style.fontWeight = style.fontWeight;
			probe.style.fontStyle = style.fontStyle;
			probe.style.fontSize = `${base}px`;
			probe.style.letterSpacing = style.letterSpacing;
			host.appendChild(probe);

			let widest = 0;
			for (const text of textsRef.current) {
				probe.textContent = "";
				for (const word of text.split(" ")) {
					const span = document.createElement("span");
					span.textContent = word;
					span.style.display = "inline-block";
					span.style.marginRight = `${WORD_GAP_EM}em`;
					probe.appendChild(span);
				}
				widest = Math.max(widest, probe.getBoundingClientRect().width);
			}

			host.removeChild(probe);
			if (!widest) return;

			// The extra pixel absorbs sub-pixel rounding, which would otherwise wrap
			// a line that measured as an exact fit.
			setFontSize(
				widest > available ? (base * (available - 1)) / widest : base,
			);
		};

		fit();

		const observer = new ResizeObserver(fit);
		observer.observe(host);

		// Poppins loads after first paint and its metrics differ from the fallback,
		// so the first measurement has to be redone once it arrives.
		void document.fonts?.ready.then(fit);

		return () => observer.disconnect();
	}, [hostRef, textsKey]);

	return fontSize;
}
